import { F0Box, F0Heading, F0Text } from '@factorialco/f0-react'
import {
  WizardStep as DeprecatedWizardStep,
  WizardStepRenderParams as DeprecatedWizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { ItemIntegrationFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import app from 'modules/core/store/models/app'
import i18n from 'modules/core/lib/i18n'

import { NetsuiteWizardData } from 'modules/integrations_netsuite/lib/types'
// eslint-disable-next-line factorial/no-allowed-mobx-collections -- Existing SettingsWizard persistence path still uses the marketplace mobx-rest store.
import { useCompanyInstallations } from 'modules/marketplace/store/collections/companyInstallations'

type NetSuiteWizardStep = DeprecatedWizardStep<NetsuiteWizardData>
type NetSuiteWizardStepRenderParams = DeprecatedWizardStepRenderParams<NetsuiteWizardData>

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <F0Box display="flex" flexDirection="column" gap="xs">
    <F0Text variant="description" color="secondary" content={label} />
    <F0Text variant="body" content={value || '-'} style={{ overflowWrap: 'anywhere' }} />
  </F0Box>
)

const VerifyStep = ({
  data,
  integration,
}: NetSuiteWizardStepRenderParams & { integration: ItemIntegrationFragment }) => {
  const session = useCurrentSession()
  const companyInstallations = useCompanyInstallations()

  useOnNext(async () => {
    if (!integration.id) {
      app.setFlash({ type: 'wrong', message: i18n.t('common.error') })
      return Promise.reject()
    }

    await companyInstallations.fetch()
    const existingInstallation = companyInstallations.find({ integration_id: integration.id })

    const payload = {
      access_id: session.access.id,
      company_id: session.company.id,
      integration_id: integration.id,
      form_data: JSON.parse(
        JSON.stringify({
          account_id: data.answers.account_id,
          client_id: data.answers.client_id,
          certificate_id: data.answers.certificate_id,
        })
      ),
    }

    const { success } = await asyncAction(async () => {
      if (existingInstallation) {
        await existingInstallation.update(payload)
      } else {
        await companyInstallations.create(payload)
      }
    })

    if (!success) {
      app.setFlash({ type: 'wrong', message: i18n.t('common.error') })
      return Promise.reject()
    }

    // Track verification here (rather than on the step's `onSubmitSuccess`) so
    // the event still fires when verifyStep is no longer the last wizard step
    // — SettingsWizard only invokes `onSubmitSuccess` on the final step.
    track(trackingEvents.integrationsNetsuite.netsuiteSettingsConnectionVerified, {
      company_id: session.company.id,
      access_id: session.access.id,
    })
  })

  return (
    <F0Box display="flex" flexDirection="row" gap="2xl">
      <F0Box display="flex" flexDirection="column" gap="xl" maxWidth="screen-sm">
        <F0Box display="flex" flexDirection="column" gap="xs">
          <F0Heading
            as="h2"
            variant="heading"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.title'
            )}
          />
          <F0Text
            variant="body"
            color="secondary"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.description'
            )}
          />
        </F0Box>
        <F0Box
          display="flex"
          flexDirection="column"
          gap="xs"
          padding="lg"
          border="default"
          borderColor="info"
          borderRadius="md"
          background="info"
        >
          <F0Text
            variant="label"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.validation_title'
            )}
          />
          <F0Text
            variant="body"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.validation_description'
            )}
          />
        </F0Box>
      </F0Box>

      <F0Box width="96">
        <F0Box
          display="flex"
          flexDirection="column"
          gap="lg"
          padding="lg"
          background="secondary"
          borderRadius="md"
        >
          <F0Text
            variant="label"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.summary_title'
            )}
          />
          <F0Box
            display="flex"
            flexDirection="column"
            gap="lg"
            padding="lg"
            border="default"
            borderColor="secondary"
            borderRadius="md"
            background="primary"
          >
            <SummaryRow
              label={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.account_id_label'
              )}
              value={data.answers.account_id || ''}
            />
            <SummaryRow
              label={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.client_id_label'
              )}
              value={data.answers.client_id || ''}
            />
            <SummaryRow
              label={i18n.t(
                'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.certificate_id_label'
              )}
              value={data.answers.certificate_id || ''}
            />
          </F0Box>
          <F0Text
            variant="body"
            color="secondary"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.summary_description'
            )}
          />
        </F0Box>
      </F0Box>
    </F0Box>
  )
}

export const verifyStep = (integration: ItemIntegrationFragment) => (): NetSuiteWizardStep => ({
  title: i18n.t(
    'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.wizard_title'
  ),
  nextText: i18n.t('common.confirm'),
  render: (props) => <VerifyStep {...props} integration={integration} />,
  isCompleted: (data) =>
    !!data.answers.account_id && !!data.answers.client_id && !!data.answers.certificate_id,
})
